import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { chromeFocusRing } from './MenuItem';
import { NavBadge, NavItemLabel } from './NavBadge';
import { activeNavTo, allNav, mainNav, utilityNav, utilityNavLabel, type NavBadges, type NavItem } from './nav';

function NavRow({
  item,
  active,
  disabled,
  count = 0,
}: {
  item: NavItem;
  active: boolean;
  disabled?: boolean;
  count?: number;
}) {
  const Icon = item.icon;

  if (disabled) {
    // Render the link visually but make it inert until a workspace exists.
    return (
      <span
        aria-disabled="true"
        tabIndex={-1}
        className="pointer-events-none flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-white/35"
      >
        <Icon className="h-[1.125rem] w-[1.125rem] shrink-0" strokeWidth={2} aria-hidden />
        <NavItemLabel item={item} count={0} className="truncate" />
      </span>
    );
  }

  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
        active
          ? 'bg-white/[.14] font-semibold text-white'
          : 'font-medium text-sidebar-text hover:bg-white/10 hover:text-white',
        chromeFocusRing,
      )}
    >
      <Icon className="h-[1.125rem] w-[1.125rem] shrink-0" strokeWidth={2} aria-hidden />
      <NavItemLabel item={item} count={count} className="truncate" />
      {item.badge && <NavBadge kind={item.badge} count={count} className="ml-auto shrink-0" />}
    </Link>
  );
}

/**
 * The left pane under the top bar, the same green gradient in both themes and 15rem wide, 240px at full
 * size (the top bar's divider lines up with its right edge at every scale). Top to bottom: the New Content button, the main group, a
 * spacer, the Configurations group pinned to the bottom, then the footer. When `disabled` (no Brand
 * Workspace yet), the pane stays visible but its links are inert and muted until the user creates
 * their first workspace.
 *
 * New Content comes in as `create` from WorkspaceGate, which owns the providers it needs: this file
 * needs none, so it renders on its own. So do the badge counts (`badges`, useNavBadges): none shows
 * without them.
 *
 * The pane itself never scrolls, so New Content and its menu are never clipped. Everything under the
 * button scrolls in its own box when the window is too short for it, so every row stays reachable on
 * a short laptop screen. That box reaches the pane's edges (`-mx-3 px-3`) and starts 2px down, because
 * it clips on both axes and the rows' 2px focus ring sits outside them.
 */
export function Sidebar({
  disabled = false,
  create,
  badges,
}: {
  disabled?: boolean;
  create?: ReactNode;
  badges?: NavBadges;
}) {
  // Worked out here, across both groups, so exactly one row is current: Scheduler Settings is a
  // view of the Campaigns & Posts page, and matching on the path alone would light both.
  const { pathname, search } = useLocation();
  const activeTo = activeNavTo(allNav, pathname, search);
  const count = (item: NavItem) => (item.badge && badges ? badges[item.badge] : 0);

  return (
    <aside className="hidden w-[15rem] shrink-0 flex-col gap-0.5 bg-gradient-to-b from-sidebar-from via-sidebar-via to-sidebar-to px-3 pb-5 pt-4 [color-scheme:dark] lg:flex">
      {create}

      <div className="-mx-3 flex min-h-0 flex-1 scroll-py-0.5 flex-col gap-0.5 overflow-y-auto px-3 pt-0.5">
        <nav className="flex flex-col gap-0.5" aria-label="Primary" aria-disabled={disabled || undefined}>
          {mainNav.map((item) => (
            <NavRow key={item.to} item={item} active={item.to === activeTo} disabled={disabled} count={count(item)} />
          ))}
        </nav>

        <div className="flex-grow" aria-hidden />

        <nav
          className="flex flex-col gap-0.5"
          aria-label={utilityNavLabel}
          aria-disabled={disabled || undefined}
        >
          <p className="px-3 pb-2 pt-7 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-sidebar-label">
            {utilityNavLabel}
          </p>
          {utilityNav.map((item) => (
            <NavRow key={item.to} item={item} active={item.to === activeTo} disabled={disabled} count={count(item)} />
          ))}
        </nav>

        <div className="mt-7 border-t border-white/[.14] px-3 pt-3.5 text-xs leading-normal text-sidebar-label">
          <p>Smorgas Social</p>
          <p>Content, scheduling &amp; analytics</p>
        </div>
      </div>
    </aside>
  );
}
